import { Link } from '@tanstack/react-router'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useCallback } from 'react'
import { useMyOrders, useCancelOrder, useVerifyPayment, type Order } from '../api/orders'
import { useAuth } from '../auth'
import { getSafePaymentActionUrl } from '../utils/paymentUrls'
import { 
  Button, 
  Heading, 
  Text, 
  Container,
  Card,
} from '@shinkansen/sakura'
import styles from './orders.module.css'

const STATUS_CONFIG: Record<string, { color: string; bgColor: string; label: string }> = {
  PENDING: {
    color: 'var(--color-status-warning)',
    bgColor: 'var(--color-status-warning-bg)',
    label: 'PENDING',
  },
  CONFIRMED: {
    color: 'var(--color-status-info)',
    bgColor: 'var(--color-status-info-bg)',
    label: 'CONFIRMED',
  },
  PREPARING: {
    color: 'var(--color-status-warning)',
    bgColor: 'var(--color-status-warning-bg)',
    label: 'PREPARING',
  },
  READY_FOR_PICKUP: {
    color: 'var(--color-accent-ink)',
    bgColor: 'var(--color-status-warning-bg)',
    label: 'READY',
  },
  IN_TRANSIT: {
    color: 'var(--color-status-info)',
    bgColor: 'var(--color-status-info-bg)',
    label: 'IN TRANSIT',
  },
  DELIVERED: {
    color: 'var(--color-status-success)',
    bgColor: 'var(--color-status-success-bg)',
    label: 'DELIVERED',
  },
  CANCELLED: {
    color: 'var(--color-status-danger)',
    bgColor: 'var(--color-status-danger-bg)',
    label: 'CANCELLED',
  },
}

const PAYMENT_STATUS_CONFIG: Record<string, { color: string; bgColor: string; label: string }> = {
  PENDING: {
    color: 'var(--color-status-warning)',
    bgColor: 'var(--color-status-warning-bg)',
    label: 'UNPAID',
  },
  PAID: {
    color: 'var(--color-status-success)',
    bgColor: 'var(--color-status-success-bg)',
    label: 'PAID',
  },
  EXPIRED: {
    color: 'var(--color-muted-foreground)',
    bgColor: 'var(--color-muted)',
    label: 'PAYMENT EXPIRED',
  },
  FAILED: {
    color: 'var(--color-status-danger)',
    bgColor: 'var(--color-status-danger-bg)',
    label: 'PAYMENT FAILED',
  },
  REFUNDED: {
    color: 'var(--color-status-info)',
    bgColor: 'var(--color-status-info-bg)',
    label: 'REFUNDED',
  },
}

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(price)
}

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function OrdersPage() {
  const { isAuthenticated, login } = useAuth()
  const { data: orders, isLoading, error, refetch } = useMyOrders()
  const cancelOrder = useCancelOrder()
  const verifyPayment = useVerifyPayment()
  
  // Track which order is waiting for payment
  const [waitingForPayment, setWaitingForPayment] = useState<string | null>(null)
  const [orderPendingCancel, setOrderPendingCancel] = useState<Order | null>(null)
  
  // Poll for payment status when waiting
  useEffect(() => {
    if (!waitingForPayment) return
    
    const pollInterval = setInterval(async () => {
      const result = await verifyPayment.mutateAsync(waitingForPayment)
      if (result && result.paymentStatus !== 'PENDING') {
        setWaitingForPayment(null)
        refetch()
      }
    }, 3000) // Poll every 3 seconds
    
    return () => clearInterval(pollInterval)
  }, [waitingForPayment, verifyPayment, refetch])
  
  // Handle PAY NOW click - open in new tab and start polling
  const handlePayNow = useCallback((orderId: string, paymentUrl: string) => {
    const safePaymentUrl = getSafePaymentActionUrl(paymentUrl)
    if (safePaymentUrl) {
      const paymentWindow = window.open(safePaymentUrl, '_blank', 'noopener,noreferrer')
      if (paymentWindow) {
        paymentWindow.opener = null
      }
    }
    setWaitingForPayment(orderId)
  }, [])

  const openCancelDialog = useCallback((order: Order) => {
    cancelOrder.reset()
    setOrderPendingCancel(order)
  }, [cancelOrder])

  const closeCancelDialog = useCallback(() => {
    if (cancelOrder.isPending) return
    cancelOrder.reset()
    setOrderPendingCancel(null)
  }, [cancelOrder])

  const handleConfirmCancel = useCallback(() => {
    if (!orderPendingCancel) return

    cancelOrder.mutate(orderPendingCancel.id, {
      onSuccess: () => {
        setOrderPendingCancel(null)
      },
    })
  }, [cancelOrder, orderPendingCancel])

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <Container size="xl">
          <div className={styles.authPrompt}>
            <span className={styles.authIcon} aria-hidden="true" />
            <Heading level="h1" size="lg">MY ORDERS</Heading>
            <Text variant="muted" className={styles.authText}>
              Please login to view your order history
            </Text>
            <Button variant="accent" size="lg" onClick={login}>
              LOGIN TO CONTINUE
            </Button>
          </div>
        </Container>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <Container size="xl">
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <Text size="xs" variant="muted" className={styles.edition}>
                TASAMAGO • ORDER HISTORY
              </Text>
              <Heading level="h1" size="xl" className={styles.title}>
                MY <span className={styles.highlight}>ORDERS</span>
              </Heading>
            </div>
            <Link to="/order/new">
              <Button variant="accent" size="lg">
                + NEW ORDER
              </Button>
            </Link>
          </div>

          {/* Stats Bar */}
          {orders && orders.length > 0 && (
            <div className={styles.statsBar}>
              <div className={styles.stat}>
                <span className={styles.statValue}>{orders.length}</span>
                <span className={styles.statLabel}>Total Orders</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>
                  {orders.filter(o => o.status === 'DELIVERED').length}
                </span>
                <span className={styles.statLabel}>Delivered</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue}>
                  {orders.filter(o => !['DELIVERED', 'CANCELLED'].includes(o.status)).length}
                </span>
                <span className={styles.statLabel}>In Progress</span>
              </div>
            </div>
          )}
        </Container>
      </header>

      {/* Content */}
      <main className={styles.content}>
        <Container size="xl">
          {isLoading ? (
            <div className={styles.loading}>
              <div className={styles.loadingEgg} />
              <Text variant="muted">LOADING ORDERS...</Text>
            </div>
          ) : error ? (
            <div className={styles.error}>
              <Text variant="muted">Failed to load orders</Text>
              <Button variant="outline" onClick={() => refetch()}>
                TRY AGAIN
              </Button>
            </div>
          ) : orders?.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyIcon} aria-hidden="true" />
              <Heading level="h2" size="md">NO ORDERS YET</Heading>
              <Text variant="muted" className={styles.emptyText}>
                Start your egg journey with your first order
              </Text>
              <Link to="/order/new">
                <Button variant="accent" size="lg">
                  PLACE FIRST ORDER
                </Button>
              </Link>
            </div>
          ) : (
            <AnimatePresence>
              <div className={styles.orderGrid}>
                {orders?.map((order, index) => {
                  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.PENDING
                  const paymentActionUrl = getSafePaymentActionUrl(order.paymentUrl)
                  
                  return (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card 
                        className={styles.orderCard}
                        padding="none"
                      >
                        {/* Order Header */}
                        <div className={styles.orderHeader}>
                          <div className={styles.orderMeta}>
                            <span className={styles.orderNumber}>
                              {order.externalId || `#${order.id.slice(0, 8).toUpperCase()}`}
                            </span>
                            <span className={styles.orderDate}>
                              {formatDate(order.createdAt)}
                            </span>
                          </div>
                          <div className={styles.orderStatusGroup}>
                            {order.paymentStatus && (
                              <span
                                className={styles.paymentStatus}
                                style={{ 
                                  backgroundColor: (PAYMENT_STATUS_CONFIG[order.paymentStatus] || PAYMENT_STATUS_CONFIG.PENDING).bgColor,
                                  color: (PAYMENT_STATUS_CONFIG[order.paymentStatus] || PAYMENT_STATUS_CONFIG.PENDING).color,
                                  borderColor: (PAYMENT_STATUS_CONFIG[order.paymentStatus] || PAYMENT_STATUS_CONFIG.PENDING).color,
                                }}
                              >
                                {(PAYMENT_STATUS_CONFIG[order.paymentStatus] || PAYMENT_STATUS_CONFIG.PENDING).label}
                              </span>
                            )}
                            <span
                              className={styles.orderStatus}
                              style={{
                                backgroundColor: statusConfig.bgColor,
                                color: statusConfig.color,
                                borderColor: statusConfig.color,
                              }}
                            >
                              {statusConfig.label}
                            </span>
                          </div>
                        </div>

                        {/* Order Body */}
                        <div className={styles.orderBody}>
                          <div className={styles.orderInfo}>
                            <div className={styles.orderRow}>
                              <span className={styles.orderLabel}>DELIVERY</span>
                              <span className={styles.orderValue}>{order.deliveryAddress}</span>
                            </div>
                            {order.estimatedDelivery && (
                              <div className={styles.orderRow}>
                                <span className={styles.orderLabel}>ETA</span>
                                <span className={styles.orderValue}>
                                  {formatDate(order.estimatedDelivery)}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className={styles.orderTotal}>
                            <span className={styles.totalLabel}>TOTAL</span>
                            <span className={styles.totalValue}>
                              {formatPrice(order.totalAmount + (order.deliveryFee || 0))}
                            </span>
                          </div>
                        </div>

                        {/* Order Footer */}
                        <div className={styles.orderFooter}>
                          {/* Waiting for payment state */}
                          {waitingForPayment === order.id ? (
                            <div className={styles.waitingPayment}>
                              <div className={styles.waitingSpinner} />
                              <span className={styles.waitingText}>Waiting for payment...</span>
                              <button
                                type="button"
                                className={styles.textLink}
                                onClick={() => setWaitingForPayment(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : order.status !== 'CANCELLED' && order.paymentStatus === 'PENDING' ? (
                            /* Unpaid order: prominent payment action with secondary actions as links */
                            <>
                              {paymentActionUrl ? (
                                <button
                                  type="button"
                                  className={styles.payNowButton}
                                  onClick={() => handlePayNow(order.id, paymentActionUrl)}
                                >
                                  <span className={styles.payNowText}>PAY NOW</span>
                                  <span className={styles.payNowAmount}>
                                    {formatPrice(order.totalAmount + (order.deliveryFee || 0))}
                                  </span>
                                  <svg className={styles.payNowArrow} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                  </svg>
                                </button>
                              ) : (
                                <Link
                                  to="/tracking/$orderId"
                                  params={{ orderId: order.id }}
                                  search={{ payment: 'pending' }}
                                  className={styles.payNowButton}
                                >
                                  <span className={styles.payNowText}>VIEW PAYMENT</span>
                                  <span className={styles.payNowAmount}>
                                    {formatPrice(order.totalAmount + (order.deliveryFee || 0))}
                                  </span>
                                  <svg className={styles.payNowArrow} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                  </svg>
                                </Link>
                              )}
                              <div className={styles.secondaryActions}>
                                <Link 
                                  to="/tracking/$orderId" 
                                  params={{ orderId: order.id }}
                                  search={{ payment: undefined }}
                                  className={styles.textLink}
                                >
                                  View Details
                                </Link>
                                <span className={styles.actionDivider}>•</span>
                                <button
                                  type="button"
                                  className={styles.textLink}
                                  onClick={() => openCancelDialog(order)}
                                  disabled={cancelOrder.isPending}
                                >
                                  {cancelOrder.isPending ? 'Cancelling...' : 'Cancel Order'}
                                </button>
                              </div>
                            </>
                          ) : (
                            /* Paid/Cancelled/Other: single track button */
                            <Link 
                              to="/tracking/$orderId" 
                              params={{ orderId: order.id }}
                              search={{ payment: undefined }}
                              className={styles.trackButton}
                            >
                              <span>{order.status === 'CANCELLED' ? 'VIEW DETAILS' : 'TRACK ORDER'}</span>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                              </svg>
                            </Link>
                          )}
                        </div>
                      </Card>
                    </motion.div>
                  )
                })}
              </div>
            </AnimatePresence>
          )}
        </Container>
      </main>

      <AnimatePresence>
        {orderPendingCancel ? (
          <motion.div
            className={styles.modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="cancel-order-title"
              className={styles.cancelModal}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
            >
              <div className={styles.cancelModalHeader}>
                <span className={styles.cancelMarker}>!</span>
                <Heading id="cancel-order-title" level="h2" size="sm" className={styles.cancelTitle}>
                  CANCEL ORDER?
                </Heading>
              </div>
              <div className={styles.cancelModalBody}>
                <Text className={styles.cancelCopy}>
                  {orderPendingCancel.externalId || `#${orderPendingCancel.id.slice(0, 8).toUpperCase()}`} will be cancelled.
                  Unpaid payment attempts are voided automatically.
                </Text>
                <div className={styles.cancelSummary}>
                  <span>TOTAL</span>
                  <strong>{formatPrice(orderPendingCancel.totalAmount + (orderPendingCancel.deliveryFee || 0))}</strong>
                </div>
                {cancelOrder.error ? (
                  <div className={styles.cancelError} role="alert">
                    Failed to cancel this order. Please try again.
                  </div>
                ) : null}
              </div>
              <div className={styles.cancelActions}>
                <button
                  type="button"
                  className={styles.keepOrderButton}
                  onClick={closeCancelDialog}
                  disabled={cancelOrder.isPending}
                >
                  KEEP ORDER
                </button>
                <button
                  type="button"
                  className={styles.cancelOrderButton}
                  onClick={handleConfirmCancel}
                  disabled={cancelOrder.isPending}
                >
                  {cancelOrder.isPending ? 'CANCELLING...' : 'CANCEL ORDER'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
